'use client';

import React, { ReactNode } from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import {
  documentToReactComponents,
  Options,
} from '@contentful/rich-text-react-renderer';
import {
  BLOCKS,
  INLINES,
  type Block,
  type Inline,
} from '@contentful/rich-text-types';
import AnimatedLink from '@/components/AnimatedLink/AnimatedLink';
import { ILink, RichTextObject } from '@/utils/common.types';
import { analytics, analyticsLinkEvent } from '@/utils/analytics';

interface Props {
  json: RichTextObject;
  links?: ILink;
  linkStyle?: string;
  dataTestId?: string;
  className?: string;
  includeSegment?: boolean;
}

const options = (
  links: ILink,
  linkStyle: string,
  includeSegment: boolean
): Options => {
  return {
    renderNode: {
      [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => (
        <h3 className={classNames('rich-text-h3', 'px-0 py-[30px]')}>
          {children}
        </h3>
      ),
      [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => (
        <p className={classNames('rich-text-p', 'break-words')}>{children}</p>
      ),
      [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: ReactNode) => (
        <li className="ml-6 list-disc">{children}</li>
      ),
      [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => (
        <ul>{children}</ul>
      ),
      [BLOCKS.EMBEDDED_ASSET]: ({ data }: Block | Inline) => {
        const asset = links?.assets?.block?.find(
          (i) => i.sys.id === data.target.sys.id
        );
        if (asset) {
          if (asset.url.startsWith('https://videos.ctfassets.net')) {
            return (
              <video src={asset?.url} title={asset?.title} muted autoPlay />
            );
          } else {
            return <img src={asset?.url} alt={asset?.title} />;
          }
        }
      },
      [INLINES.ENTRY_HYPERLINK]: (
        node: Block | Inline,
        children: ReactNode
      ) => {
        const isOpenNewWindow = node?.data?.target?.slug?.startsWith('http');
        return (
          <Link
            href={node?.data?.target?.slug}
            target={isOpenNewWindow ? '_blank' : '_self'}
            rel={isOpenNewWindow ? 'noopener noreferrer' : ''}
            onClick={() =>
              includeSegment &&
              analytics?.track('CTA Clicked', {
                isNav: true,
                type: 'text',
                text: node?.data?.target?.slug,
              })
            }
          >
            {children}
          </Link>
        );
      },
      [INLINES.HYPERLINK]: ({ data }: Block | Inline, children: ReactNode) => {
        const videoData = { className: 'rich-text-embed', videoUrl: data.uri };
        const isOpenNewWindow = data?.uri?.startsWith('http');
        if (videoData?.videoUrl?.startsWith('https://www.youtube.com/embed')) {
          return (
            <span
              className={classNames(
                'responsive-embed',
                'relative block w-full pb-[56.25%]'
              )}
            >
              <iframe
                className={classNames(
                  'youtube-player',
                  'absolute left-0 top-0 h-full w-full'
                )}
                src={videoData.videoUrl}
                allowFullScreen
                allow="encrypted-media"
                loading="lazy"
              ></iframe>
            </span>
          );
        }
        return linkStyle === 'button' && children ? (
          <AnimatedLink
            buttonOpenNewWindow={isOpenNewWindow}
            buttonUrl={data.uri}
            onClick={() =>
              includeSegment && isOpenNewWindow
                ? analytics?.track('External Link Clicked', {
                    isNav: true,
                    type: 'rich_text_link',
                  })
                : analyticsLinkEvent(data, children)
            }
            title={children[0]}
            linkWrapper="span"
          />
        ) : (
          <Link
            href={data.uri}
            onClick={() =>
              includeSegment && isOpenNewWindow
                ? analytics?.track('External Link Clicked', {
                    isNav: true,
                    type: 'rich_text_link',
                  })
                  : analyticsLinkEvent(data, children)
            }
            target={isOpenNewWindow ? '_blank' : '_self'}
            rel={isOpenNewWindow ? 'noopener noreferrer' : ''}
          >
            {children}
          </Link>
        );
      },
    },
  };
};

// Returns a React Component.
const generateDocument = ({
  json,
  links,
  linkStyle = 'standard',
  includeSegment = false,
}) => {
  if (json)
    return documentToReactComponents(
      json,
      options(links, linkStyle, includeSegment)
    );
};

// Wrap the component in the class name.
const ContentfulRichText = ({
  json,
  links,
  linkStyle = 'standard',
  dataTestId,
  className,
  includeSegment,
}: Props) => {
  return (
    <div className="mx-auto my-0 max-w-standard">
      <div
        data-testid={dataTestId}
        className={classNames('contentful-rich-text', {
          [`${className}`]: className,
        })}
      >
        {generateDocument({ json, links, linkStyle, includeSegment })}
      </div>
    </div>
  );
};

export default ContentfulRichText;
